import { Paper, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { type MouseEvent, useRef, useState } from "react";
import {
  BooleanInput,
  Create,
  ListButton,
  NumberInput,
  required,
  SaveButton,
  SimpleForm,
  TextInput,
  Toolbar,
  TopToolbar,
  useNotify,
  useRedirect,
} from "react-admin";
import { useFormContext } from "react-hook-form";

import { MODULE_NAMES } from "@/constants/modules";
import { campaignTdsSchema } from "@/modules/campaignTds";
import {
  type CampaignTdsRecord,
  CampaignTdsCreate,
} from "@/modules/campaignTds/components/CampaignTdsCreate";
import { type CampaignTdsRecordsToCreate } from "@/modules/campaignTds/components/CampaignTdsCreateFormModal";
import { type CampaignTdsRecordToEdit } from "@/modules/campaignTds/components/CampaignTdsEditFormModal";
import { ClientAppInput } from "@/modules/clientApps";
import { DeviceTypeInput } from "@/modules/deviceTypes/components/DeviceTypeInput";
import { TrackerInput } from "@/modules/trackers";
import { LIST_QUERY_KEY } from "@/utils/query";

import { TrafficSourceInput } from "../../trafficSources/components/TrafficSourceInput";
import { campaignsSchema } from "../api";
import { DEFAULT_CAMPAIGN_CLIENT_APP_ID, REQUIRED_FIELDS } from "../constants";

type CreateToolbarProps = {
  tds: CampaignTdsRecord[];
  isDisabled?: boolean;
  onChangeIsSubmitting: (sSubmitting: boolean) => void;
  onResetTds: () => void;
};

const CreateToolbar = ({
  tds,
  isDisabled,
  onChangeIsSubmitting,
  onResetTds,
}: CreateToolbarProps) => {
  const { reset, getValues, trigger } = useFormContext();
  const notify = useNotify();
  const redirect = useRedirect();
  const queryClient = useQueryClient();

  const createCampaign = async () => {
    if (!campaignsSchema.create) {
      throw new Error("Create campaign method is not implemented");
    }

    const createTds = campaignTdsSchema.create;

    if (!createTds) {
      throw new Error("Create campaign TDS method is not implemented");
    }

    const isValid = await trigger(REQUIRED_FIELDS);

    if (!isValid) {
      throw new Error("Please fill in all required fields");
    }
    const values = getValues();

    const createdCampaign = await campaignsSchema.create({
      data: values,
    });

    const campaignId = createdCampaign.data["@id"];

    const tdsResponses = await Promise.allSettled(
      tds.map((item) =>
        createTds({
          data: {
            ...item,
            campaign: campaignId,
          },
        }),
      ),
    );

    const hasFailedRequests = tdsResponses.some(
      (response) => response.status === "rejected",
    );

    if (hasFailedRequests) {
      notify("Some tds not created", {
        type: "error",
      });
    }

    void queryClient.invalidateQueries({
      queryKey: [MODULE_NAMES.CAMPAIGN_TDS, LIST_QUERY_KEY],
    });
    void queryClient.invalidateQueries({
      queryKey: [MODULE_NAMES.CAMPAIGNS, LIST_QUERY_KEY],
    });
  };

  const handleCreate = async (
    event: MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    event.preventDefault();

    try {
      onChangeIsSubmitting(true);
      await createCampaign();

      notify("Campaign created!");
      onResetTds();
      redirect("list", MODULE_NAMES.CAMPAIGNS);
    } catch (error) {
      notify((error as Error).message, { type: "error" });
    } finally {
      onChangeIsSubmitting(false);
    }
  };

  const handleCreateAndMore = async (
    event: MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    event.preventDefault();

    try {
      onChangeIsSubmitting(true);
      await createCampaign();

      notify("Campaign created!");
      reset(getValues());
    } catch (error) {
      notify((error as Error).message, { type: "error" });
    } finally {
      onChangeIsSubmitting(false);
    }
  };

  return (
    <Toolbar sx={{ gap: 2 }}>
      <SaveButton
        label="Create"
        type="button"
        disabled={isDisabled}
        onClick={(event) => {
          void handleCreate(event);
        }}
      />
      <SaveButton
        label="Create And Add More"
        type="button"
        disabled={isDisabled}
        onClick={(event) => {
          void handleCreateAndMore(event);
        }}
      />
      <ListButton label="Cancel" icon={null} variant="outlined" size="medium" />
    </Toolbar>
  );
};

const CreateActions = () => (
  <TopToolbar sx={{ justifyContent: "flex-start" }}>
    <ListButton />
  </TopToolbar>
);

export const CampaignCreate = () => {
  const idCounter = useRef(1);
  const [campaignTds, setCampaignTds] = useState<CampaignTdsRecord[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCreateCampaigns = (record: CampaignTdsRecordsToCreate) => {
    const campaigns: CampaignTdsRecord[] = record.landings.map((landing) => {
      const newId = idCounter.current;
      idCounter.current++;

      return {
        id: newId,
        landing,
        countrySet: record.countrySet,
        landings: record.landings,
        modelSet: record.modelSet,
        osSet: record.osSet,
      };
    });

    setCampaignTds((previousCampaigns) => [...previousCampaigns, ...campaigns]);
  };

  const handleEditCampaign = (record: CampaignTdsRecordToEdit) => {
    setCampaignTds((previousTds) => {
      const newTds = [...previousTds];
      const indexToEdit = newTds.findIndex(({ id }) => id === record.id);

      newTds[indexToEdit] = record;

      return newTds;
    });
  };

  const handleResetTds = () => {
    setCampaignTds([]);
  };

  return (
    <Create actions={<CreateActions />} redirect="list">
      <SimpleForm
        disabled={isSubmitting}
        toolbar={
          <CreateToolbar
            isDisabled={isSubmitting}
            tds={campaignTds}
            onChangeIsSubmitting={setIsSubmitting}
            onResetTds={handleResetTds}
          />
        }
        direction="column"
        gap={2}
        alignItems="stretch"
      >
        <Paper
          sx={{
            flex: 1,
            padding: 2,
            width: "100%",
          }}
          elevation={1}
          variant="outlined"
        >
          <ClientAppInput
            source="clientAppId"
            optionValue="id"
            defaultValue={DEFAULT_CAMPAIGN_CLIENT_APP_ID}
            validate={[required()]}
            filter={{ deprecated: false }}
          />
          <TextInput
            source="title"
            helperText="example: Best of The Best"
            validate={[required()]}
          />
          <DeviceTypeInput source="deviceTypeId" />
          <TrafficSourceInput source="trafficSource" validate={[required()]} />
          <TrackerInput source="trackerId" validate={[required()]} />
          <NumberInput
            source="campaignId"
            label="Campaign Id"
            defaultValue={0}
            validate={[required()]}
          />

          <Stack direction="column" spacing={2} width="100%">
            <Typography variant="h3" fontSize="16px">
              Install Postback Url
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <BooleanInput
                source="installPostbackUrlEnabled"
                label={false}
                defaultValue={false}
              />
              <TextInput
                fullWidth
                source="installPostbackUrl"
                defaultValue="http://swtchry.com/click.php?cnv_id={click_id}&payout=1.5"
              />
            </Stack>
          </Stack>

          <Stack direction="column" spacing={2} width="100%">
            <Typography variant="h3" fontSize="16px">
              Trial Postback Url
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <BooleanInput
                source="trialPostbackUrlEnabled"
                label={false}
                defaultValue={false}
              />
              <TextInput
                fullWidth
                source="trialPostbackUrl"
                defaultValue="http://swtchry.com/click.php?cnv_id={click_id}&payout=1.5"
              />
            </Stack>
          </Stack>

          <Stack direction="column" spacing={2} width="100%">
            <Typography variant="h3" fontSize="16px">
              Subscription Postback Url
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <BooleanInput
                source="subscriptionPostbackUrlEnabled"
                label={false}
                defaultValue={false}
              />
              <TextInput
                fullWidth
                source="subscriptionPostbackUrl"
                defaultValue="http://swtchry.com/click.php?cnv_id={click_id}&payout=1.5"
              />
            </Stack>
          </Stack>

          <BooleanInput source="active" defaultValue={true} />
        </Paper>
        <Paper
          sx={{
            flex: 1,
            padding: 2,
            width: "100%",
          }}
          elevation={1}
          variant="outlined"
        >
          <CampaignTdsCreate
            data={campaignTds}
            isDisabled={isSubmitting}
            onCreateCampaigns={handleCreateCampaigns}
            onEditCampaign={handleEditCampaign}
          />
        </Paper>
      </SimpleForm>
    </Create>
  );
};
